import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import ProductGenres from '../ProductGenres';
import ProductAge from '../ProductAge';
import { ProductFormat } from '../ProductFormat';
import ProductPublisher from '../ProductPublisher';
import { ProductReleaseDate } from '../ProductReleaseDate';
import ProductUsageConditions from '../ProductUsageConditions';
import ProductLanguage from '../ProductLanguage';
import { concreteLoanFormatNameToKey, LOANFORMAT_EPRESS } from '../../../store/loanFormat/actions';
import { CAMPAIGN_PRODUCTS, isFeatureEnabled } from '../../utils/togglz';
import LinkWithIconRight from '../../common/LinkWithIconRight';
import { THEME_PRIMARY } from '../../../styles/basics/colors';
import { generateRelatedBundlesForAuthors, generateRelatedBundlesForProduct } from '../../utils/utils-related-bundle';
import { AddProduct } from '../../common/AddProduct/AddProduct';
import { Article } from '../../../../models/Article';
import { ProductMetaData } from '../../../../models/Product';
import { ButtonSize, ButtonTheme } from '../../common/Button';
import { ReleaseFrequency } from '../../common/ReleaseFrequency';
import { useTranslation } from 'react-i18next';
import { SingleAddOptions } from '../../../../models/ListContentCheckOptions';

const SidebarContainer = styled.div`
    width: 215px;
    flex-grow: 0;
`;

const StLinkContainer = styled.div`
    margin-bottom: 15px;
`;

type Props = {
    readonly product: ProductMetaData & Article;
    readonly onAddPrice: (options: SingleAddOptions) => Promise<void>;
    readonly onSelectPrice: () => Promise<void>;
};

const ProductDetailSidebar: FC<Props> = props => {
    const { t } = useTranslation();
    //TODO: Remove Feature toggle
    const isCampaignsEnabled = isFeatureEnabled(CAMPAIGN_PRODUCTS);

    const { product } = props;
    let relatedBundles: ReactNode = null;

    const relatedBundlesByAuthorInner = generateRelatedBundlesForAuthors(
        product.relatedBundlesByAuthor,
        product.authors
    );
    const relatedBundlesByProductInner = generateRelatedBundlesForProduct(
        product.relatedBundlesByProduct,
        product.titleWithoutPrefix
    );

    if (relatedBundlesByAuthorInner || relatedBundlesByProductInner) {
        relatedBundles = (
            <div className="bundles">
                <h4>{t('menu.shop.loanFormat.curatedCollections')}</h4>
                {relatedBundlesByProductInner}
                {relatedBundlesByAuthorInner}
            </div>
        );
    }

    const releasedLabel =
        !isEmpty(product.salesStatus) && product.salesStatus!.key === 'AVAILABLE'
            ? t('components.filterBar.filterGroup.released')
            : '';
    const loanFormatKey = concreteLoanFormatNameToKey(product.loanFormat);

    const campaignLink = !(isCampaignsEnabled && product.campaignAvailable) ? null : (
        <StLinkContainer>
            <LinkWithIconRight
                to="/unlimited/campaigns/all"
                label="Also available as campaign product"
                icon="chevron_right"
                iconColor={THEME_PRIMARY}
            />
        </StLinkContainer>
    );

    return (
        <SidebarContainer className="ca-side">
            <div className="ca-side-inner">
                <AddProduct
                    size={ButtonSize.MEDIUM}
                    theme={ButtonTheme.POSITIVE}
                    product={product}
                    onAddPrice={props.onAddPrice}
                    onSelectPrice={props.onSelectPrice}
                    showSelectedPriceOnly={false}
                    allowSelection={true}
                />

                {campaignLink}
                <ProductFormat
                    product={product}
                    showIcon={true}
                    loanFormatKey={loanFormatKey}
                    shopLanguage={undefined}
                />
                {loanFormatKey === LOANFORMAT_EPRESS ? (
                    <ReleaseFrequency product={product} />
                ) : (
                    <ProductReleaseDate product={product} label={releasedLabel} />
                )}
                <ProductAge product={product} loanFormatKey={loanFormatKey} shopLanguage={undefined} />
                <ProductLanguage
                    product={product}
                    loanFormatKey={loanFormatKey}
                    label={product.language!}
                    shopLanguage={undefined}
                />
                <ProductUsageConditions
                    product={product}
                    showDetails={true}
                    loanFormatKey={loanFormatKey}
                    shopLanguage={undefined}
                />
                <p>{product.isbn13}</p>
                <div className="genres">
                    <h4>{t('menu.shop.loanFormat.genres')}</h4>
                    <ProductGenres
                        product={product}
                        showGenresInSeparateLines="true"
                        showDetails={true}
                        loanFormatKey={loanFormatKey}
                        shopLanguage={undefined}
                    />
                </div>
                <div className="publishers">
                    <h4>{t('menu.shop.loanFormat.publishers')}</h4>
                    <ProductPublisher product={product} loanFormatKey={loanFormatKey} shopLanguage={undefined} />
                </div>
                {relatedBundles}
            </div>
        </SidebarContainer>
    );
};

export default ProductDetailSidebar;
